import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Mail, ArrowLeft, Home as HomeIcon, Send, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Seo } from "../components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.message.trim()) { setError("Fyll i ditt namn och ett meddelande."); return; }
    if (!validEmail) { setError("Skriv in en giltig e-postadress."); return; }
    setSubmitting(true);
    try {
      await api.post("/contact", { name: form.name, email: form.email, message: form.message });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Något gick fel. Försök igen om en stund.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title="Kontakta oss"
        description="Har du frågor om våra manualer, kurser eller tjänster? Kontakta Enkla Manualer via formulär, e-post (support@enklamanualer.se) eller livechatt. Vi svarar samma dag."
        path="/kontakt"
        jsonLd={{ "@context": "https://schema.org", "@type": "ContactPage", name: "Kontakta oss", url: "https://enklamanualer.se/kontakt" }}
      />
      <Link to="/" data-testid="contact-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till start
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <Mail size={44} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-1" /> Kontakta Enkla Manualer
      </h1>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Har du frågor om våra manualer, digitala kurser, vill du boka en hemkurs eller har du egna
        önskemål om något annat du behöver hjälp med?
      </p>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Skriv till oss via formuläret nedan, skicka ett
        mail, eller använd vår livechatt som du hittar som en blå chattbubbla längst ner i det högra
        hörnet på skärmen för att prata direkt med vår mänskliga support.
      </p>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Vi har en fast svarsgaranti och återkopplar alltid till dig samma dag.
      </p>

      {done ? (
        <div data-testid="contact-success" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-8 text-center">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4">
            <CheckCircle2 size={56} strokeWidth={2} />
          </span>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-5">Tack för ditt meddelande!</h2>
          <p className="text-lg text-[#4A4A4A] mt-3">
            Vi har tagit emot ditt meddelande och återkopplar till dig samma dag på den e-postadress du angav.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-6">
            <HomeIcon size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-8">
          <div className="space-y-6">
            <div>
              <label htmlFor="c-name" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Namn</label>
              <input id="c-name" data-testid="contact-name" value={form.name} onChange={set("name")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="För- och efternamn" />
            </div>
            <div>
              <label htmlFor="c-email" className="text-xl font-bold mb-2 block text-[#1A1A1A]">E-postadress</label>
              <input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="namn@exempel.se" />
            </div>
            <div>
              <label htmlFor="c-message" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Meddelande</label>
              <textarea id="c-message" data-testid="contact-message" value={form.message} onChange={set("message")} rows={6}
                className="text-lg px-4 py-3 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2] resize-y"
                placeholder="Beskriv vad du behöver hjälp med…" />
            </div>

            {error && (
              <p data-testid="contact-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold">
                <AlertCircle size={22} /> {error}
              </p>
            )}

            <button data-testid="contact-submit" type="submit" disabled={submitting}
              className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
              {submitting ? <Loader2 className="animate-spin" size={26} /> : <Send size={26} strokeWidth={2.5} />}
              {submitting ? "Skickar…" : "Skicka meddelande"}
            </button>
          </div>
        </form>
      )}

      <p className="text-lg text-[#1A1A1A] mt-8 text-center">
        Föredrar du att maila direkt? Du når oss på{" "}
        <a href="mailto:support@enklamanualer.se" data-testid="contact-email-link" className="font-bold text-[#0056D2] hover:underline">
          support@enklamanualer.se
        </a>.
      </p>

      <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed mt-4 text-center">
        Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
      </p>
    </div>
  );
}
